import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  TemplateRef,
  computed,
  input,
  output,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';

import { EmptyStateComponent } from '../empty-state/empty-state.component';
import { ErrorStateComponent } from '../error-state/error-state.component';

/** Description d'une colonne du tableau. */
export interface DataColumn {
  /** Clé logique — sert d'identifiant de tri (émis tel quel au parent). */
  key: string;
  /** Clé i18n de l'en-tête. */
  labelKey: string;
  /** La colonne est-elle triable ? Défaut : non. */
  sortable?: boolean;
  /** Alignement du contenu ; défaut : début (start). */
  align?: 'start' | 'center' | 'end';
  /** Colonne numérique (chiffres tabulaires, alignés à droite). */
  numeric?: boolean;
  /** Largeur CSS optionnelle (ex. `'160px'`, `'20%'`). */
  width?: string;
  /** Colonne d'actions : épinglée à l'inline-end pour rester visible sans
   *  défilement horizontal (tableaux larges sur petits écrans). */
  action?: boolean;
  /** Colonne secondaire masquée quand la largeur *disponible* (container query,
   *  donc barre latérale prise en compte) descend sous un seuil :
   *  `'md'` ≈ écrans étroits, `'lg'` masquée plus tôt. */
  hideBelow?: 'md' | 'lg';
}

/** État de pagination serveur, quand la table est paginée. */
export interface DataTablePage {
  currentPage: number;
  totalPages: number;
  total: number;
}

/**
 * Tableau de données générique, **présentationnel**.
 *
 * <p>Extrait du style de la banque de questions pour être réutilisé : en-têtes
 * triables, squelette de chargement, états vide et erreur, pagination
 * optionnelle. Il ne connaît pas la source des données — le parent fournit les
 * lignes, l'état, et un gabarit de ligne (`rowTemplate`) qui rend les cellules
 * `<td>`. Aucune dépendance à une grille tierce, RTL natif via propriétés
 * logiques.</p>
 *
 * <p>Le tri et la pagination sont *pilotés par le parent* : la table émet
 * l'intention (colonne triée, page demandée) et se contente d'afficher l'état
 * qu'on lui passe. Elle convient donc aussi bien à un tri client qu'à une
 * recherche paginée côté serveur.</p>
 */
@Component({
  selector: 'app-data-table',
  standalone: true,
  imports: [
    NgTemplateOutlet,
    TranslateModule,
    EmptyStateComponent,
    ErrorStateComponent,
  ],
  templateUrl: './data-table.component.html',
  styleUrl: './data-table.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataTableComponent {
  readonly columns = input.required<DataColumn[]>();
  readonly rows = input.required<readonly unknown[]>();
  /** Gabarit de ligne : reçoit `{ $implicit: row, index }` et rend les `<td>`. */
  readonly rowTemplate = input.required<TemplateRef<unknown>>();

  /** Propriété servant de clé de suivi pour `@for`. Défaut : `id`. */
  readonly trackKey = input<string>('id');

  readonly loading = input<boolean>(false);
  readonly errored = input<boolean>(false);
  readonly errorTitleKey = input<string>('DATA_TABLE.ERROR_TITLE');
  readonly errorMessageKey = input<string>('DATA_TABLE.ERROR_MESSAGE');
  readonly errorDetail = input<string>('');

  readonly emptyIcon = input<string>('folder-open');
  readonly emptyTitleKey = input<string>('DATA_TABLE.EMPTY_TITLE');
  readonly emptyMessageKey = input<string>('DATA_TABLE.EMPTY_MESSAGE');

  readonly sortBy = input<string | null>(null);
  readonly sortDescending = input<boolean>(false);

  readonly skeletonRows = input<number>(8);

  /** Pagination serveur ; laisser à `null` pour une table non paginée. */
  readonly page = input<DataTablePage | null>(null);
  readonly pageSize = input<number>(25);
  readonly pageSizes = input<number[]>([25, 50, 100]);
  readonly showPager = input<boolean>(false);

  readonly sortChange = output<string>();
  readonly pageChange = output<number>();
  readonly pageSizeChange = output<number>();
  readonly retry = output<void>();

  readonly skeletonRowList = computed(() =>
    Array.from({ length: this.skeletonRows() }, (_, i) => i),
  );
  readonly skeletonCells = computed(() =>
    Array.from({ length: this.columns().length }, (_, i) => i),
  );

  /** Succès sans résultat : état vide, jamais de contenu de repli. */
  readonly isEmpty = computed(
    () => !this.loading() && !this.errored() && this.rows().length === 0,
  );

  readonly currentPage = computed(() => this.page()?.currentPage ?? 1);
  readonly totalPages = computed(() => this.page()?.totalPages ?? 0);
  readonly canPrevious = computed(
    () => !this.loading() && this.currentPage() > 1,
  );
  readonly canNext = computed(
    () => !this.loading() && this.currentPage() < this.totalPages(),
  );

  onSort(column: DataColumn): void {
    if (column.sortable) this.sortChange.emit(column.key);
  }

  isSortedBy(column: DataColumn): boolean {
    return this.sortBy() === column.key;
  }

  sortIcon(column: DataColumn): string {
    if (!this.isSortedBy(column)) return 'fa-sort';
    return this.sortDescending() ? 'fa-sort-down' : 'fa-sort-up';
  }

  previousPage(): void {
    if (this.canPrevious()) this.pageChange.emit(this.currentPage() - 1);
  }

  nextPage(): void {
    if (this.canNext()) this.pageChange.emit(this.currentPage() + 1);
  }

  onPageSize(raw: string): void {
    const size = Number(raw);
    if (size) this.pageSizeChange.emit(size);
  }

  /** Piste de suivi pour `@for` : la clé configurée, à défaut l'index. */
  trackRow = (index: number, row: unknown): unknown => {
    const key = this.trackKey();
    const value = (row as Record<string, unknown>)?.[key];
    return value ?? index;
  };
}
